// Premium UX layer — smooth scroll, text reveal, counters, cursor, splash.
// Everything is desktop-first with mobile fallbacks and reduced-motion respect.

import { useEffect, useRef, useState } from 'react';
import { useContent } from './content.jsx';

/* =================== Smooth scroll (Lenis) =================== */

export function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Skip smooth scroll on touch / low-power devices — they already have native inertia.
    if (window.matchMedia('(hover: none)').matches) return;

    let raf;
    let lenis;
    let onClick;

    (async () => {
      const { default: Lenis } = await import('lenis');
      lenis = new Lenis({
        lerp: 0.1,
        smoothWheel: true,
        wheelMultiplier: 1,
        syncTouch: false,
      });
      const frame = (time) => {
        lenis.raf(time);
        raf = requestAnimationFrame(frame);
      };
      raf = requestAnimationFrame(frame);

      onClick = (e) => {
        const a = e.target.closest('a[href^="#"]');
        if (!a) return;
        const href = a.getAttribute('href');
        if (!href || href === '#' || href.startsWith('#/')) return;
        const el = document.querySelector(href);
        if (!el) return;
        e.preventDefault();
        lenis.scrollTo(el, { offset: -60, duration: 1.0 });
      };
      document.addEventListener('click', onClick);
      window.__lenis = lenis;
    })();

    return () => {
      if (onClick) document.removeEventListener('click', onClick);
      cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
      window.__lenis = undefined;
    };
  }, []);
  return null;
}

/* =================== Text reveal (split per word) =================== */

export function RevealText({ as: Tag = 'span', children, delay = 0, stagger = 0.04, className = '', style = {}, ...rest }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setShown(true); return; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  const parts = splitWords(children);

  return (
    <Tag ref={ref} className={className} style={style} {...rest}>
      {parts.map((tok, i) => (
        tok.isSpace ? <span key={i} aria-hidden> </span> : (
          <span key={i} className="reveal-word" aria-hidden={false}
            style={{
              display: 'inline-block',
              overflow: 'hidden',
              verticalAlign: 'top',
            }}>
            <span
              style={{
                display: 'inline-block',
                transform: shown ? 'none' : 'translateY(115%)',
                opacity: shown ? 1 : 0,
                filter: shown ? 'blur(0)' : 'blur(6px)',
                transition: `transform 1s cubic-bezier(.22,.61,.36,1) ${delay + i * stagger}s, opacity .9s ease ${delay + i * stagger}s, filter .8s ease ${delay + i * stagger}s`,
              }}>
              {tok.text}
            </span>
          </span>
        )
      ))}
    </Tag>
  );
}

function splitWords(children) {
  const out = [];
  const push = (node) => {
    if (node == null || node === false) return;
    if (typeof node === 'string' || typeof node === 'number') {
      const s = String(node);
      const parts = s.split(/(\s+)/);
      for (const p of parts) {
        if (!p) continue;
        if (/^\s+$/.test(p)) out.push({ isSpace: true });
        else out.push({ isSpace: false, text: p });
      }
      return;
    }
    if (Array.isArray(node)) { node.forEach(push); return; }
    // React element — keep as-is (no split into it); render it entirely
    out.push({ isSpace: false, text: node });
  };
  push(children);
  return out;
}

/* =================== Count-up number =================== */

export function Counter({ value, duration = 1600, decimals, prefix = '', suffix = '', className = '' }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);

  const { num, dec, pre, suf } = parseNumber(value);
  const targetDec = decimals != null ? decimals : dec;

  useEffect(() => {
    if (!ref.current) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setDisplay(num); return; }
    let raf;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const step = (t) => {
        const p = Math.min(1, (t - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(num * eased);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, { threshold: 0.3 });
    io.observe(ref.current);
    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [num, duration]);

  const formatted = display.toFixed(targetDec);
  return (
    <span ref={ref} className={className}>
      {prefix || pre}{formatted}{suffix || suf}
    </span>
  );
}

function parseNumber(raw) {
  const s = String(raw);
  // Find number and capture prefix/suffix (e.g. "%100" → pre="%", num=100)
  const m = s.match(/^(\D*?)(-?\d+(?:[.,]\d+)?)(.*)$/);
  if (!m) return { num: 0, dec: 0, pre: '', suf: '' };
  const numStr = m[2].replace(',', '.');
  const dec = (numStr.split('.')[1] || '').length;
  return { num: Number(numStr), dec, pre: m[1], suf: m[3] };
}

/* =================== Custom cursor (desktop only) =================== */

export function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    if (!mq.matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let x = -100, y = -100;
    let rx = x, ry = y;
    let hover = false;
    let raf = null;
    let running = false;

    const updateDot = () => {
      if (dotRef.current) dotRef.current.style.transform = `translate3d(${x - 3}px, ${y - 3}px, 0)`;
    };

    const loop = () => {
      const dx = x - rx, dy = y - ry;
      rx += dx * 0.22;
      ry += dy * 0.22;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${rx - 12}px, ${ry - 12}px, 0) scale(${hover ? 1.6 : 1})`;
      }
      if (Math.abs(dx) > 0.2 || Math.abs(dy) > 0.2) {
        raf = requestAnimationFrame(loop);
      } else {
        raf = null;
        running = false;
      }
    };

    const kick = () => {
      if (!running) {
        running = true;
        raf = requestAnimationFrame(loop);
      }
    };

    const onMove = (e) => { x = e.clientX; y = e.clientY; updateDot(); kick(); };
    const onEnter = (e) => {
      if (e.target.closest?.('a, button, [role="button"], input, textarea, select, [data-cursor="hover"]')) {
        hover = true;
        document.body.classList.add('cursor-hover');
        kick();
      }
    };
    const onLeave = (e) => {
      if (e.target.closest?.('a, button, [role="button"], input, textarea, select, [data-cursor="hover"]')) {
        hover = false;
        document.body.classList.remove('cursor-hover');
        kick();
      }
    };

    document.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseover', onEnter, { passive: true });
    document.addEventListener('mouseout', onLeave, { passive: true });
    document.body.classList.add('has-custom-cursor');

    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseover', onEnter);
      document.removeEventListener('mouseout', onLeave);
      document.body.classList.remove('has-custom-cursor', 'cursor-hover');
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <div ref={ringRef} className="glamm-cursor-ring" aria-hidden/>
      <div ref={dotRef}  className="glamm-cursor-dot" aria-hidden/>
    </>
  );
}

/* =================== Splash intro =================== */

export function Splash() {
  const c = useContent();
  const brand = c.brand || {};
  const [phase, setPhase] = useState('in'); // 'in' | 'out' | 'gone'
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (sessionStorage.getItem('glamm:splashed') === '1') {
      setPhase('gone');
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setPhase('gone');
      sessionStorage.setItem('glamm:splashed', '1');
      return;
    }
    // Lock scroll while splash is showing
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    let p = 0;
    const tick = setInterval(() => {
      p = Math.min(100, p + (5 + Math.random() * 12));
      setProgress(p);
      if (p >= 100) {
        clearInterval(tick);
        setTimeout(() => setPhase('out'), 300);
        setTimeout(() => { setPhase('gone'); document.body.style.overflow = prev; }, 1400);
        sessionStorage.setItem('glamm:splashed', '1');
      }
    }, 110);
    return () => { clearInterval(tick); document.body.style.overflow = prev; };
  }, []);

  if (phase === 'gone') return null;

  return (
    <div className={`glamm-splash phase-${phase}`} aria-hidden>
      <div className="glamm-splash-bg"/>
      <div className="glamm-splash-center">
        <div className="glamm-splash-rings">
          <span/><span/><span/>
        </div>
        <div className="glamm-splash-logo">
          <img src={brand.logo || '/assets/glamm.png'} alt="" width="104" height="104"/>
        </div>
        <h1 className="glamm-splash-name">
          {'GLAMM'.split('').map((ch, i) => (
            <span key={i} style={{ animationDelay: `${0.4 + i * 0.08}s` }}>{ch}</span>
          ))}
        </h1>
        <div className="glamm-splash-tag">Coffee &amp; Bakery</div>
      </div>
      <div className="glamm-splash-bar"><div style={{ width: progress + '%' }}/></div>
      <div className="glamm-splash-curtain-top"/>
      <div className="glamm-splash-curtain-bottom"/>
    </div>
  );
}

/* =================== Parallax hover wrapper =================== */

export function ParallaxCard({ children, intensity = 12, className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current; if (!el) return;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    if (!mq.matches) return;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width  - 0.5) * 2;
      const y = ((e.clientY - rect.top)  / rect.height - 0.5) * 2;
      el.style.setProperty('--px', (x * intensity) + 'px');
      el.style.setProperty('--py', (y * intensity) + 'px');
      el.style.setProperty('--rx', (-y * 4) + 'deg');
      el.style.setProperty('--ry', (x * 4) + 'deg');
    };
    const onLeave = () => {
      el.style.setProperty('--px', '0px');
      el.style.setProperty('--py', '0px');
      el.style.setProperty('--rx', '0deg');
      el.style.setProperty('--ry', '0deg');
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [intensity]);

  return (
    <div ref={ref} className={`parallax-card ${className}`}
      style={{ perspective: '1000px' }}>
      {children}
    </div>
  );
}
